/**
 * The bell a score row shows after its status column (ISSUE-979), from 640px
 * up only. At phone width there is no bell on a row: alerts live on the game
 * header, which frees the 44px for team names (Scores UX spec v1.0 section
 * 1.7). Width driven, the one anatomy difference the spec allows.
 *
 * The row's star and its Follow teams dialog were removed by ISSUE-1081
 * (owner, 2026-10-08), so following a team lives on the team page and on
 * Account.
 *
 * The column is one `touchTarget` wide in pixels (44px), because
 * `scoreRowGeometry.ts` subtracts exactly `SCORE_ROW_BELL_COLUMN_PX` from the
 * name cell at 640px and up, and any other width would make that wrong.
 *
 * The bell is filled, in the primary color, when the fan has alerts on for
 * the game (ISSUE-1391, v1 `score_tile.dart`): `/event/list`,
 * `/event/week-list` and `/feed/upcoming` all send `hasAlertsOn`, read once
 * per response on the server, never per row. Saving an alert in the sheet
 * refetches those reads, so the bell settles to the server's value.
 *
 * The handler stops propagation before opening its overlay, and the button
 * carries relative z-10, so it sits above the row's game target (ISSUE-980)
 * and a tap on it never also opens the game.
 */
import { cn } from "@sportsmet/ui";
import type { MouseEvent, ReactNode } from "react";

import { BellIcon, ICON_BUTTON_CLASS } from "../../icons/actionIcons";
import { useOverlay } from "../../routeState/useOverlay";
import type { typeScoresEventInput } from "./scoreRowModel";

export const ROW_ALERTS_SHEET = "row-alerts";

export function rowAlertsLabel(
  awayName: string,
  homeName: string,
  isOn = false,
): string {
  const label = `Alerts for ${awayName} at ${homeName}`;
  return isOn ? `${label}, on` : label;
}

export type typeScoreRowActionsProps = { event: typeScoresEventInput };

export const ScoreRowActions = (props: typeScoreRowActionsProps): ReactNode => {
  const overlay = useOverlay();
  const isOn = props.event.hasAlertsOn === true;

  const handleAlertsClick = (
    clickEvent: MouseEvent<HTMLButtonElement>,
  ): void => {
    clickEvent.stopPropagation();
    overlay.open({
      kind: "sheet",
      name: ROW_ALERTS_SHEET,
      params: { alertsEventId: props.event.eventId },
    });
  };

  return (
    <div
      className="hidden shrink-0 items-center sm:flex"
      data-testid="score-row-actions"
    >
      <button
        type="button"
        data-testid="score-row-alerts"
        data-alerts-on={isOn ? "true" : undefined}
        aria-label={rowAlertsLabel(
          props.event.away.name,
          props.event.home.name,
          isOn,
        )}
        // relative z-10 lifts the bell above ScoreRow's row wide game target (ISSUE-980); without it a tap on it opens the game.
        className={cn(
          ICON_BUTTON_CLASS,
          "relative z-10",
          isOn ? "text-primary" : "text-ink-secondary",
        )}
        onClick={handleAlertsClick}
      >
        <BellIcon isFilled={isOn} />
      </button>
    </div>
  );
};
